"use client";

import React from "react";
import Link from "next/link";
import type { AirQualityLeaderboardEntry } from "@/lib/conditions/air-quality";

function aqiBadgeColor(aqi: number): string {
  if (aqi <= 20) return "bg-emerald-100 text-emerald-800";
  if (aqi <= 40) return "bg-lime-100 text-lime-800";
  if (aqi <= 60) return "bg-amber-100 text-amber-800";
  if (aqi <= 80) return "bg-orange-100 text-orange-800";
  return "bg-red-100 text-red-800";
}

function locationHref(entry: AirQualityLeaderboardEntry): string {
  return `/conditions/${entry.slug}`;
}

function locationTypeLabel(type: string): string {
  if (type === "ski") return "Mountain";
  return "Coastal";
}

type SortKey = "aqi" | "pm25" | "pm10" | "no2" | "o3" | "so2";

export function AirQualityTable({
  entries,
  countries,
}: {
  entries: AirQualityLeaderboardEntry[];
  countries: string[];
}) {
  const displayTypes = [...new Set(entries.map((e) => locationTypeLabel(e.locationType)))].sort();
  const [countryFilter, setCountryFilter] = React.useState("all");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState<SortKey>("aqi");
  const [sortAsc, setSortAsc] = React.useState(true);

  const filtered = entries
    .filter((e) => countryFilter === "all" || e.country === countryFilter)
    .filter((e) => typeFilter === "all" || locationTypeLabel(e.locationType) === typeFilter)
    .sort((a, b) => {
      const diff = (a[sortKey] ?? 0) - (b[sortKey] ?? 0);
      return sortAsc ? diff : -diff;
    });

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  }

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? " ↑" : " ↓") : "";

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="text-[13px] px-3 py-2 rounded-[10px] border border-border-light bg-white text-navy font-medium"
          aria-label="Filter by country"
        >
          <option value="all">All countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {displayTypes.length > 1 && (
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-[13px] px-3 py-2 rounded-[10px] border border-border-light bg-white text-navy font-medium"
            aria-label="Filter by location type"
          >
            <option value="all">All locations</option>
            {displayTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
        <span className="text-[12px] text-text-muted self-center ml-auto">
          {filtered.length} location{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-[16px] border border-border-light bg-white">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-warm-bg sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px] w-8">#</th>
              <th className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px]">Location</th>
              <th className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px]">Country</th>
              <th className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px]">Type</th>
              <th
                className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px] cursor-pointer hover:text-navy select-none"
                onClick={() => handleSort("aqi")}
              >
                AQI{sortIndicator("aqi")}
              </th>
              <th className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px]">Rating</th>
              <th
                className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px] cursor-pointer hover:text-navy select-none"
                onClick={() => handleSort("pm25")}
              >
                PM2.5{sortIndicator("pm25")}
              </th>
              <th
                className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px] cursor-pointer hover:text-navy select-none"
                onClick={() => handleSort("pm10")}
              >
                PM10{sortIndicator("pm10")}
              </th>
              <th
                className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px] cursor-pointer hover:text-navy select-none"
                onClick={() => handleSort("no2")}
              >
                NO2{sortIndicator("no2")}
              </th>
              <th
                className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px] cursor-pointer hover:text-navy select-none"
                onClick={() => handleSort("o3")}
              >
                O3{sortIndicator("o3")}
              </th>
              <th
                className="px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px] cursor-pointer hover:text-navy select-none"
                onClick={() => handleSort("so2")}
              >
                SO2{sortIndicator("so2")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-light">
            {filtered.map((entry, i) => (
              <tr key={entry.slug} className="hover:bg-warm-bg/50 transition-colors">
                <td className="px-4 py-3.5 tabular-nums text-text-muted font-medium">{i + 1}</td>
                <td className="px-4 py-3.5">
                  <Link
                    href={locationHref(entry)}
                    className="font-semibold text-navy no-underline hover:text-gold transition-colors"
                  >
                    {entry.name}
                  </Link>
                </td>
                <td className="px-4 py-3.5 text-text-muted">{entry.country}</td>
                <td className="px-4 py-3.5 text-text-muted">{locationTypeLabel(entry.locationType)}</td>
                <td className="px-4 py-3.5 tabular-nums font-bold text-navy">{entry.aqi}</td>
                <td className="px-4 py-3.5">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${aqiBadgeColor(entry.aqi)}`}>
                    {entry.aqiLabel}
                  </span>
                </td>
                <td className="px-4 py-3.5 tabular-nums text-text-body">{entry.pm25.toFixed(1)}</td>
                <td className="px-4 py-3.5 tabular-nums text-text-body">{entry.pm10.toFixed(1)}</td>
                <td className="px-4 py-3.5 tabular-nums text-text-body">{entry.no2.toFixed(1)}</td>
                <td className="px-4 py-3.5 tabular-nums text-text-body">{entry.o3.toFixed(1)}</td>
                <td className="px-4 py-3.5 tabular-nums text-text-body">{entry.so2.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((entry, i) => (
          <Link
            key={entry.slug}
            href={locationHref(entry)}
            className="block bg-white rounded-[14px] border border-border-light p-4 no-underline hover:border-emerald-200 hover:shadow-[0_4px_16px_rgba(52,211,153,0.06)] transition-all"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="text-[11px] text-text-muted font-medium tabular-nums mr-2">#{i + 1}</span>
                <span className="font-semibold text-navy text-[15px]">{entry.name}</span>
                <div className="text-[12px] text-text-muted mt-0.5">
                  {entry.country} · {locationTypeLabel(entry.locationType)}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[20px] font-bold text-navy tabular-nums">{entry.aqi}</div>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${aqiBadgeColor(entry.aqi)}`}>
                  {entry.aqiLabel}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2 mt-3 pt-3 border-t border-border-light/60">
              <div>
                <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">PM2.5</div>
                <div className="text-[13px] font-semibold text-navy tabular-nums">{entry.pm25.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">PM10</div>
                <div className="text-[13px] font-semibold text-navy tabular-nums">{entry.pm10.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">NO2</div>
                <div className="text-[13px] font-semibold text-navy tabular-nums">{entry.no2.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">O3</div>
                <div className="text-[13px] font-semibold text-navy tabular-nums">{entry.o3.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">SO2</div>
                <div className="text-[13px] font-semibold text-navy tabular-nums">{entry.so2.toFixed(1)}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
